/**
 * BoardHeader — sticky `.bhdr` per the canonical wireframe
 * (docs/wireframes/celestia/03-screen.html, `.bhdr` section).
 *
 * Row 1: player dot carousel (.pdots) + menu button (.hbtn)
 * Row 2: phase badge (.hc.turn) + mini city track (.ctrk.mini) + turn counter
 * Collapsible detail: player table (.ptab) + recent log (.log)
 * Connection indicator: near menu button (data-testid="connection-status")
 */

import type { ViewerState } from "@cloudnest/celestia-engine";
import { useEffect, useRef, useState } from "react";
import type { UseLocaleReturn } from "../i18n/useLocale";
import {
  PHASE_BADGE,
  PHASE_KIND,
  seatLabel,
  seatVar,
} from "../screen-constants";
import type { CelestiaConnectionStatus } from "./types";

/** City value-tier data from the wireframe. */
const CITY_NODES: ReadonlyArray<{
  readonly id: number;
  readonly label: string;
  readonly bg: string;
  readonly dk?: boolean;
}> = [
  { id: 1, label: "1+", bg: "var(--cv-1)" },
  { id: 2, label: "2+", bg: "var(--cv-2)" },
  { id: 4, label: "4+", bg: "var(--cv-4)" },
  { id: 6, label: "6+", bg: "var(--cv-6)" },
  { id: 9, label: "9+", bg: "var(--cv-9)" },
  {
    id: 12,
    label: "12+",
    bg: "linear-gradient(180deg,var(--cv-12b),var(--cv-12a))",
    dk: true,
  },
  { id: 15, label: "15", bg: "var(--cv-15)" },
  { id: 20, label: "20", bg: "var(--cv-20)", dk: true },
  { id: 25, label: "25", bg: "var(--cv-25)", dk: true },
];

function cityIndex(cityId: number): number {
  // map actual city to nearest tier index
  for (let i = CITY_NODES.length - 1; i >= 0; i--) {
    const node = CITY_NODES[i];
    if (node !== undefined && cityId >= node.id) return i;
  }
  return 0;
}

interface PlayerViewModel {
  readonly playerId: string;
  readonly seat: number;
  readonly displayName: string;
  readonly isMe: boolean;
  readonly isCaptain: boolean;
  readonly status: "acting" | "wait" | "off" | "dc";
  readonly isOnAircraft: boolean;
  readonly handCount: number;
  readonly treasureCount: number;
}

interface Props {
  readonly viewerState: ViewerState;
  readonly myPlayerId: string | null;
  readonly connectionStatus: CelestiaConnectionStatus;
  readonly i18n: UseLocaleReturn;
  readonly onMenuOpen: () => void;
}

export function BoardHeader({
  viewerState,
  myPlayerId,
  connectionStatus,
  i18n,
  onMenuOpen,
}: Props) {
  const { t } = i18n;
  const [open, setOpen] = useState(false);
  const trackRef = useRef<HTMLElement>(null);

  const aircraft = viewerState.vessels.find((v) => v.kind === "aircraft");
  const currentCity = aircraft?.currentCity ?? 1;
  const captainPlayerId = aircraft?.captainPlayerId ?? "";
  const phaseId = viewerState.currentPhase.phase;
  const badge = PHASE_BADGE[phaseId] ?? "P?";
  const kind = PHASE_KIND[phaseId] ?? "neutral";
  const legalActors = viewerState.currentPhase.legalActorSet;

  const players: PlayerViewModel[] = viewerState.players.map((p) => {
    const isActing = legalActors.includes(p.playerId);
    const isDc = p.connectionStatus === "disconnected";
    const isOff = !p.isOnAircraft && !isDc;

    let status: PlayerViewModel["status"] = "wait";
    if (isDc) status = "dc";
    else if (isOff) status = "off";
    else if (isActing) status = "acting";

    return {
      playerId: p.playerId,
      seat: p.seat,
      displayName: p.displayName,
      isMe: p.playerId === myPlayerId,
      isCaptain: p.playerId === captainPlayerId,
      status,
      isOnAircraft: p.isOnAircraft,
      handCount: p.handCount,
      treasureCount: p.treasureCount,
    };
  });

  const curIdx = cityIndex(currentCity);

  // Center the track on the current city node
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-centers after the curIdx-driven [data-ccur] node is committed
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const row = el.querySelector<HTMLElement>(".row");
    if (!row) return;
    const cur = row.querySelector<HTMLElement>("[data-ccur]");
    if (!cur) return;
    const off = cur.offsetLeft + cur.offsetWidth / 2 - el.clientWidth / 2;
    row.style.transform = `translateX(${-off}px)`;
  }, [curIdx]);

  function playerStatus(p: PlayerViewModel): string {
    if (p.status === "dc") return "연결끊김";
    if (!p.isOnAircraft) return "이탈";
    if (p.isCaptain) return "선장·탑승";
    return "승객·탑승";
  }

  return (
    <header className={open ? "bhdr open" : "bhdr"}>
      {/* Row 1: player dots + menu */}
      <div className="r">
        <span className="hph-spacer" aria-hidden="true" />
        <span className="spacer" />
        {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: <span> used for inline layout; aria-label announces the dot group to screen readers */}
        <span className="pdots" aria-label="플레이어">
          {players.map((p) => (
            <span
              key={p.playerId}
              className={`pd ${p.status}`}
              style={{ background: seatVar(p.seat) }}
              title={`${seatLabel(p.seat)} ${p.displayName}${p.isCaptain ? " 선장" : ""}${p.status === "dc" ? " 연결끊김" : ""}`}
            >
              {seatLabel(p.seat)}
              {p.isCaptain && (
                <span className="hat">
                  <svg className="ico" aria-hidden="true">
                    <use href="#ic-caphat" />
                  </svg>
                </span>
              )}
              <svg className="tring" viewBox="0 0 34 34" aria-hidden="true">
                <circle cx="17" cy="17" r="15" />
              </svg>
            </span>
          ))}
        </span>
        <span className="spacer" />
        {/* Connection status indicator */}
        <span
          data-testid="connection-status"
          data-status={
            connectionStatus === "connected" ? "connected" : "reconnecting"
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            height: 20,
            padding: "0 6px",
            borderRadius: "var(--cn-radius-pill)",
            fontSize: 9,
            fontWeight: 800,
            background:
              connectionStatus === "connected"
                ? "color-mix(in srgb,var(--cn-celestia-score-winner) 20%,transparent)"
                : "color-mix(in srgb,var(--cn-celestia-deadline-ring) 20%,transparent)",
            color:
              connectionStatus === "connected"
                ? "var(--cn-celestia-score-winner)"
                : "var(--cn-celestia-deadline-ring)",
            marginRight: 4,
          }}
        >
          {connectionStatus === "connected"
            ? t("conn_connected")
            : t("conn_reconnecting")}
        </span>
        <button
          className="hbtn"
          aria-label="메뉴"
          title="메뉴"
          type="button"
          onClick={onMenuOpen}
        >
          ⋯
        </button>
      </div>

      {/* Row 2: phase badge + mini city track + turn counter */}
      <div className="r r2">
        <span
          className={`hc turn ${kind}`}
          data-testid="celestia-phase-badge"
          title="현재 단계(Phase)"
        >
          {badge}
        </span>

        <section
          className="ctrk mini"
          ref={trackRef}
          data-testid="celestia-city-track"
          aria-label="항해 중인 도시"
        >
          <div className="row">
            {CITY_NODES.map((node, i) => {
              const diff = i - curIdx;
              let cls = "cn";
              if (i === curIdx) cls += " cur";
              else if (i < curIdx) cls += " reached";
              else if (Math.abs(diff) === 1) cls += " n1";
              else if (Math.abs(diff) === 2) cls += " n2";

              return (
                <span
                  key={node.id}
                  className={cls}
                  {...(i === curIdx ? { "data-ccur": "" } : {})}
                >
                  <span
                    className={node.dk ? "cd dk" : "cd"}
                    style={{ background: node.bg }}
                  >
                    {node.label}
                  </span>
                </span>
              );
            })}
          </div>
        </section>

        <span className="hc" title="턴 카운터">
          턴 {viewerState.windowSeq}
        </span>
      </div>

      {/* Collapsible detail */}
      <div className="hdr-detail">
        <table className="ptab">
          <thead>
            <tr>
              <th>플레이어</th>
              <th>상태</th>
              <th>손패</th>
              <th>보물</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr
                key={p.playerId}
                className={
                  legalActors.includes(p.playerId)
                    ? "turn"
                    : p.status === "off"
                      ? "off"
                      : p.status === "dc"
                        ? "dc"
                        : ""
                }
              >
                <td>
                  <span className="av" style={{ background: seatVar(p.seat) }}>
                    {seatLabel(p.seat)}
                    {p.isCaptain && (
                      <span className="hat">
                        <svg className="ico" aria-hidden="true">
                          <use href="#ic-caphat" />
                        </svg>
                      </span>
                    )}
                  </span>
                  {p.displayName}
                  {p.isMe ? " (나)" : ""}
                </td>
                <td>{playerStatus(p)}</td>
                <td>
                  {p.isOnAircraft ? (
                    p.isMe ? (
                      <>
                        <span className="lock" /> {p.handCount}
                      </>
                    ) : (
                      p.handCount
                    )
                  ) : (
                    "—"
                  )}
                </td>
                <td>{p.treasureCount > 0 ? `◆${p.treasureCount}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Derived from the public ViewerState — no event history is
            projected, so this shows the current situation, not a fake log. */}
        <section className="log" aria-label="현재 상황">
          <div className="e">
            <span className="w">{badge}</span>
            <span>
              <span className="a">{t(`phase_${phaseId}`)}</span>
            </span>
          </div>
          <div className="e">
            <span className="w">행동</span>
            <span>
              {legalActors.length === 0
                ? "시스템 진행"
                : legalActors
                    .map((id) => {
                      const pl = players.find((p) => p.playerId === id);
                      return pl
                        ? `${seatLabel(pl.seat)} ${pl.displayName}`
                        : id;
                    })
                    .join(", ")}
            </span>
          </div>
          <div className="e">
            <span className="w">덱</span>
            <span>
              남은 {viewerState.mainDeck.drawCount} · 버림{" "}
              {viewerState.mainDeck.discardCount}
            </span>
          </div>
        </section>
      </div>

      <button
        type="button"
        className="hdr-grip"
        aria-label="헤더 세부 펼치기/접기"
        onClick={() => setOpen((v) => !v)}
      >
        세부{" "}
        <span className="chev" aria-hidden="true">
          ▾
        </span>
      </button>
    </header>
  );
}
